import React, { useEffect, useState } from "react";
import { alpha, useTheme } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import useMediaQuery from "@mui/material/useMediaQuery";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";

import TermsCheckbox from "./TermsCheckbox";

const OPTION_LABEL_SX = { ml: 0, mr: 0, alignItems: "center" };
const OPTION_RADIO_SX = { p: 0.75, mr: 0.5 };

/**
 * Confirmation step shown before a purchase. Confirming is what actually
 * creates the invoice and moves the client to payment — or, with a wallet
 * picked from `wallets`, pays it outright. `onConfirm` receives `{ voucherId }`.
 */
function PurchaseConfirmDialog({
  open,
  name,
  lines = [],
  note,
  location,
  price,
  regularPrice = null,
  currency = "",
  notices = [],
  wallets = [],
  loading = false,
  onConfirm,
  onClose
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(true);
  const [walletId, setWalletId] = useState("");

  // A wallet that left the list (balance spent elsewhere, family member switched)
  // must not ride along unseen.
  const chosenWallet = wallets.find((w) => w._id === walletId) || null;

  const rules = notices.filter(Boolean);
  const termsCheckbox = (
    <TermsCheckbox
      checked={termsAccepted}
      onChange={setTermsAccepted}
      disabled={loading}
      suffix={rules.length > 0 ? `and the booking rule${rules.length === 1 ? "" : "s"} above` : undefined}
      sx={{ mt: rules.length > 0 ? 0 : 2 }}
    />
  );

  // The dialog instance is reused for every plan on the list, so consent given
  // for one purchase must not carry over to the next one opened. The rules
  // reopen with it: unlike the picker panel, which remembers being collapsed,
  // these are what the tick box below accepts — folding them away is a way to
  // reach the pay button once they have been read, never a way to skip them.
  useEffect(() => {
    if (open) {
      setTermsAccepted(false);
      setRulesOpen(true);
      setWalletId("");
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      {/* Full screen leaves no backdrop to tap and no Cancel button below, so
          dismissal needs a control of its own. */}
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
        Confirm purchase
        <IconButton size="small" onClick={onClose} disabled={loading} aria-label="Close" sx={{ mr: -0.5 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {chosenWallet ? "Confirming pays from your wallet — no card payment needed." : "Confirming creates an invoice and takes you to payment."}
        </Typography>
        <Box sx={{ p: 2, borderRadius: 2, border: "1px solid", borderColor: "divider", bgcolor: (t) => alpha(t.palette.primary.main, 0.03) }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {name}
          </Typography>
          {lines.filter(Boolean).map((line, i) => (
            <Typography key={i} variant="body2" color="text.secondary">
              {line}
            </Typography>
          ))}
          {location && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}>
              <PlaceOutlinedIcon sx={{ fontSize: "0.9375rem", color: "text.secondary" }} />
              <Typography variant="body2" color="text.secondary">
                {location}
              </Typography>
            </Box>
          )}
          {/* A caveat about how the purchase behaves, not a spec of what is being bought —
              set apart so it is not read as another line item. */}
          {note && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1, fontStyle: "italic" }}>
              {note}
            </Typography>
          )}
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1.5}>
            <Typography variant="body2" fontWeight={600}>
              Total
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
              {Number(regularPrice) > Number(price || 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ textDecoration: "line-through" }}>
                  {currency} {Number(regularPrice).toFixed(2)}
                </Typography>
              )}
              <Typography variant="h6" fontWeight={700} color="primary.main">
                {currency} {Number(price || 0).toFixed(2)}
              </Typography>
            </Box>
          </Box>
          {wallets.length > 0 && (
            <Box sx={{ mt: 1.5 }}>
              <Divider sx={{ mb: 1.5 }} />
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.75,
                  mb: 0.5
                }}>
                <AccountBalanceWalletOutlinedIcon sx={{ fontSize: "0.875rem" }} />
                Pay with
              </Typography>
              <RadioGroup value={chosenWallet ? chosenWallet._id : ""} onChange={(e) => setWalletId(e.target.value)}>
                <FormControlLabel
                  value=""
                  disabled={loading}
                  sx={OPTION_LABEL_SX}
                  control={<Radio size="small" sx={OPTION_RADIO_SX} />}
                  label={
                    <Typography variant="body2" fontWeight={500}>
                      Card
                    </Typography>
                  }
                />
                {wallets.map((w) => (
                  <FormControlLabel
                    key={w._id}
                    value={w._id}
                    disabled={loading}
                    sx={OPTION_LABEL_SX}
                    control={<Radio size="small" sx={OPTION_RADIO_SX} />}
                    label={
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {w.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Balance {currency} {(parseFloat(w.remainValue) || 0).toFixed(2)}
                          {w.noExpiry ? " • No expiry" : w.expiryDate ? ` • Expires ${new Date(w.expiryDate).toLocaleDateString()}` : ""}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </RadioGroup>
            </Box>
          )}
        </Box>
        {/* Rules the client is accepting by paying, as opposed to `note`, which
            only describes how the purchase behaves. They share a box with the
            tick box that consents to them, so "above" can only mean these and
            the consent reads as one act rather than two stray controls.
            Carries the same blue as the panel on the picker, because it carries
            the same rules — a client who read them there should recognise them
            here rather than weigh them a second time. */}
        {rules.length > 0 ? (
          <Box
            sx={{
              mt: 2,
              p: 2,
              pt: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: (t) => alpha(t.palette.info.main, 0.35),
              bgcolor: (t) => alpha(t.palette.info.main, 0.08),
              // The tick box is shared with checkouts that sit on plain white,
              // where its brand-green link is right. In here that green is the
              // one thing on the panel not speaking in the panel's colour, so
              // the consent row is tinted at the wall rather than in the
              // component every other caller would inherit.
              "& .MuiFormControlLabel-label": { color: "info.dark" },
              "& .MuiLink-root": { color: "info.dark" },
              "& .MuiCheckbox-root": { color: (t) => alpha(t.palette.info.main, 0.6) },
              "& .MuiCheckbox-root.Mui-checked": { color: "info.main" }
            }}>
            <Box
              role="button"
              tabIndex={0}
              aria-expanded={rulesOpen}
              onClick={() => setRulesOpen((prev) => !prev)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  setRulesOpen((prev) => !prev);
                }
              }}
              sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, cursor: "pointer", userSelect: "none" }}>
              <Typography variant="subtitle2" fontWeight={700} color="info.dark">
                Before you pay
              </Typography>
              <ExpandMoreIcon
                fontSize="small"
                sx={{ color: "info.dark", flexShrink: 0, transition: "transform 150ms", transform: rulesOpen ? "rotate(180deg)" : "none" }}
              />
            </Box>
            <Collapse in={rulesOpen}>
              {rules.length === 1 ? (
                <Typography variant="body2" color="info.dark" sx={{ mt: 0.75 }}>
                  {rules[0]}
                </Typography>
              ) : (
                <Box
                  component="ul"
                  sx={{ m: 0, mt: 0.75, pl: 2.5, listStyleType: "disc", "& > li": { display: "list-item" }, "& > li + li": { mt: 0.75 } }}>
                  {rules.map((rule, i) => (
                    <Typography key={i} component="li" variant="body2" color="info.dark">
                      {rule}
                    </Typography>
                  ))}
                </Box>
              )}
            </Collapse>
            {/* The tick box never collapses with the rules — it is the control the
                client has to reach, and a heading with nothing under it reads as a
                dead panel. */}
            <Divider sx={{ mt: rulesOpen ? 1.5 : 1, mb: 0.5, borderColor: (t) => alpha(t.palette.info.main, 0.25) }} />
            {termsCheckbox}
          </Box>
        ) : (
          termsCheckbox
        )}
      </DialogContent>
      {isMobile && <Divider />}

      {/* On a phone the pair of small right-aligned buttons becomes one full-width
          CTA sitting at the bottom of the screen — Cancel drops out because the
          title's close control already covers it. disableSpacing: DialogActions
          otherwise indents every sibling after the first, which pulls a stretched
          button off the left edge. */}
      <DialogActions disableSpacing sx={{ px: 3, pt: isMobile ? 2 : 0, pb: 2.5, gap: 1, justifyContent: "flex-end" }}>
        {!isMobile && (
          <Button size="small" color="error" onClick={onClose} disabled={loading} sx={{ textTransform: "none" }}>
            Cancel
          </Button>
        )}
        <Button
          size={isMobile ? "large" : "small"}
          variant="contained"
          fullWidth={isMobile}
          onClick={() => onConfirm?.({ voucherId: chosenWallet?._id || null })}
          disabled={loading || !termsAccepted}
          sx={{ textTransform: "none", color: "common.white", minWidth: "6.5rem", fontWeight: 700, boxShadow: "none" }}
        >
          {loading ? <CircularProgress size="1.125rem" /> : chosenWallet ? "Pay with wallet" : "Confirm & pay"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PurchaseConfirmDialog;
